import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import FilterListIcon from '@mui/icons-material/FilterList';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Popover from '@mui/material/Popover';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useRouteSelectionLogic } from './useRouteSelectionLogic';
import RouteSelectionTable from './RouteSelection';
import { useDispatch } from 'react-redux';
import { loadGPXFile } from '../charts/ChartsSlice';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Link from '@mui/material/Link';

function RouteSelection({ setIsLoading, isLoading = false }) {
    const {
        searchTerm,
        routeIdError,
        loadingRouteId,
        routeIdDialogOpen,
        anchorEl,
        hasRouteIdInput,
        open,
        connectionsLoaded,
        routeIdInputRef,
        handleSearch,
        handleFilterClick,
        handleFilterClose,
        handleFilterChange,
        handleLoadRouteById,
        handleRouteIdKeyDown,
        handleOpenRouteIdDialog,
        handleCloseRouteIdDialog,
        handleRouteIdInputChange,
    } = useRouteSelectionLogic();

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const filters = useSelector(state => state.routeSelection.filters);
    const gpxError = useSelector(state => state.charts.gpxError);
    const connections = useSelector(state => state.auth.connections);

    // Show loading state while waiting for auth
    if (!connectionsLoaded && navigator.onLine) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                maxWidth: { xs: '100%', sm: '800px' },
                mx: 'auto',
                width: '100%',
                py: 4
            }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Loading your routes...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Please wait while we connect to Ride With GPS
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            maxWidth: { xs: '100%', sm: '800px' },
            mx: 'auto',
            width: '100%',
            overflow: 'hidden',
            boxSizing: 'border-box'
        }}>
            <Box sx={{
                px: { xs: 0.5, sm: 2 },
                py: { xs: 0.5, sm: 2 },
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                width: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden'
            }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search routes..."
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch(e.target.value);
                            }
                        }}
                        InputProps={{
                            endAdornment: searchTerm && (
                                <IconButton
                                    size="small"
                                    onClick={() => handleSearch('')}
                                    sx={{ mr: 0.5 }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            ),
                        }}
                    />
                    <input
                        type="file"
                        accept=".gpx"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                                setIsLoading(true);
                                const reader = new FileReader();
                                reader.onload = async (event) => {
                                    try {
                                        const gpxData = event.target.result;
                                        const result = await dispatch(loadGPXFile({ gpxData, fileName: file.name })).unwrap();
                                        navigate(`/cues/gpx/${result.routeId}`);
                                    } catch (error) {
                                        console.error('Error loading GPX file:', error);
                                    } finally {
                                        setIsLoading(false);
                                        if (fileInputRef.current) {
                                            fileInputRef.current.value = '';
                                        }
                                    }
                                };
                                reader.readAsText(file);
                            }
                        }}
                    />
                    <Tooltip title="Upload GPX file">
                        <IconButton
                            size="small"
                            onClick={() => fileInputRef.current?.click()}
                            color="primary"
                        >
                            <UploadFileIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Load route by ID">
                        <IconButton
                            size="small"
                            onClick={handleOpenRouteIdDialog}
                            color="primary"
                        >
                            <LocationOnIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Filter routes">
                        <IconButton
                            size="small"
                            onClick={handleFilterClick}
                            color={Object.values(filters).some(value => value !== 'all') ? 'primary' : 'default'}
                        >
                            <FilterListIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Route ID Dialog */}
            <Dialog
                open={routeIdDialogOpen}
                onClose={handleCloseRouteIdDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Load Route by ID</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Enter a Ride With GPS route ID to load it directly
                    </Typography>
                    <TextField
                        fullWidth
                        size="medium"
                        placeholder="Route ID (e.g., 12345)"
                        variant="outlined"
                        inputRef={routeIdInputRef}
                        onChange={handleRouteIdInputChange}
                        onKeyDown={handleRouteIdKeyDown}
                        error={!!routeIdError}
                        helperText={routeIdError}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseRouteIdDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleLoadRouteById}
                        disabled={loadingRouteId || !hasRouteIdInput}
                    >
                        {loadingRouteId ? 'Loading...' : 'Load Route'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Popover
                anchorEl={anchorEl}
                open={open}
                onClose={handleFilterClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: { p: 2 }
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl component="fieldset">
                        <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 1 }}>Cache Status</FormLabel>
                        <RadioGroup
                            row
                            value={filters.cacheStatus}
                            onChange={(e) => handleFilterChange('cacheStatus', e.target.value)}
                        >
                            <FormControlLabel
                                value="all"
                                control={<Radio size="small" />}
                                label={<Typography variant="body2">All</Typography>}
                            />
                            <FormControlLabel
                                value="cached"
                                control={<Radio size="small" />}
                                label={<Typography variant="body2">Cached</Typography>}
                            />
                            <FormControlLabel
                                value="uncached"
                                control={<Radio size="small" />}
                                label={<Typography variant="body2">Uncached</Typography>}
                            />
                        </RadioGroup>
                    </FormControl>
                    <FormControl component="fieldset">
                        <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 1 }}>Provider</FormLabel>
                        <RadioGroup
                            row
                            value={filters.provider}
                            onChange={(e) => handleFilterChange('provider', e.target.value)}
                        >
                            <FormControlLabel
                                value="all"
                                control={<Radio size="small" />}
                                label={<Typography variant="body2">All</Typography>}
                            />
                            <FormControlLabel
                                value="rwgps"
                                control={<Radio size="small" />}
                                label={<Typography variant="body2">RWGPS</Typography>}
                            />
                            <FormControlLabel
                                value="strava"
                                control={<Radio size="small" />}
                                label={<Typography variant="body2">Strava</Typography>}
                            />
                            <FormControlLabel
                                value="gpx"
                                control={<Radio size="small" />}
                                label={<Typography variant="body2">GPX</Typography>}
                            />
                        </RadioGroup>
                    </FormControl>
                    {/* Add more filter groups here in the future */}
                </Box>
            </Popover>
            <Box sx={{ flex: 1, overflow: 'hidden', width: '100%' }}>
                {connectionsLoaded && !connections.rwgps && !connections.strava ? (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        py: 4,
                        px: 2
                    }}>
                        <Alert severity="info" sx={{ mb: 2, maxWidth: '600px', width: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                Connect a route source
                            </Typography>
                            <Typography variant="body2">
                                You can view routes from GPX files (click the <UploadFileIcon /> icon), but for a more seamless experience, <Link href="/settings">connect a route source</Link> like Ride With GPS or Strava.
                            </Typography>
                        </Alert>
                    </Box>
                ) : (
                    <RouteSelectionTable setIsLoading={setIsLoading} isLoading={isLoading} />
                )}
            </Box>
            <Snackbar
                open={!!gpxError}
                autoHideDuration={6000}
            >
                <Alert severity="error" sx={{ width: '100%' }}>
                    {gpxError}
                </Alert>
            </Snackbar>
        </Box>
    );
}


export default RouteSelection; 